import { useLingui } from '@lingui/react/macro'
import { ChooseEditScopePanel } from 'features/knowledgeDocumentViewer/components/metadata/ChooseEditScopePanel'
import { MetadataEditFormChrome } from 'features/knowledgeDocumentViewer/components/metadata/MetadataEditFormChrome'
import { PageMetadataContent } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataContent'
import { PageRevisionFamilyDropdown } from 'features/knowledgeDocumentViewer/components/metadata/PageRevisionFamilyDropdown'
import {
  saveMetadataEditWithScope,
  useMetadataEditScopeFlow,
} from 'features/knowledgeDocumentViewer/hooks/useMetadataEditScopeFlow'
import { usePageMetadataEditForm } from 'features/knowledgeDocumentViewer/hooks/usePageMetadataEditForm'
import { usePageRevisionFamily } from 'features/knowledgeDocumentViewer/hooks/usePageRevisionFamily'
import type {
  DocumentMetadataUserEditEntry,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import type { FamilyPageMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyPageMetadataUserEditPuts'
import { mergeDocumentMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { PAGE_REVISION_CODE_FIELD_LABEL } from 'features/knowledgeDocumentViewer/utils/pageMetadataFieldKeys'
import type {
  PageRevisionFamilyMember,
  RevisionFamilyMemberView,
} from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import React, { useCallback } from 'react'

export type PageMetadataEditSectionProps = {
  pageEntry: SystemDocumentPageEntry | undefined
  pageUserEdit: DocumentPageMetadataUserEditEntry | undefined
  pageNum: number
  isLoading: boolean
  canEdit: boolean
  isSaving: boolean
  currentBqDocumentId: string | undefined
  currentFilename: string | undefined
  systemDocument?: SystemDocumentEntry
  documentUserEdit?: DocumentMetadataUserEditEntry
  onSave: (values: DocumentPageMetadataUserEditValues) => Promise<void>
  onSaveFamily: (
    values: DocumentPageMetadataUserEditValues,
    members: readonly FamilyPageMetadataSaveMember[]
  ) => Promise<void>
  onEditingChange?: (isEditing: boolean) => void
  onSelectVersion?: (member: RevisionFamilyMemberView) => void
  onOpenInNewTab?: (member: RevisionFamilyMemberView) => void
  includeInternalIds?: boolean
  showRevisionDropdown?: boolean
}

export function PageMetadataEditSection({
  pageEntry,
  pageUserEdit,
  pageNum,
  isLoading,
  canEdit,
  isSaving,
  currentBqDocumentId,
  currentFilename,
  systemDocument,
  documentUserEdit,
  onSave,
  onSaveFamily,
  onEditingChange,
  onSelectVersion,
  onOpenInNewTab,
  includeInternalIds = false,
  showRevisionDropdown = false,
}: PageMetadataEditSectionProps): React.ReactElement {
  const { t } = useLingui()
  const userEditValues = pageUserEdit?.entry.data.values ?? undefined
  const documentBaselineValues = mergeDocumentMetadataValues(
    systemDocument?.entry.data,
    documentUserEdit?.entry.data.values
  )

  const {
    members,
    priorEditsByPageEntryId,
    documentsByBqId,
    priorDocumentEditsByBqDocumentId,
    mutatePriorEdits,
    arePriorEditsReady,
    hasRevisionFamily,
    familySize,
    isLoading: isFamilyLoading,
  } = usePageRevisionFamily({
    pageEntry,
    pageUserEdit,
    currentBqDocumentId,
    currentPageNum: pageNum,
    enabled: canEdit || showRevisionDropdown,
  })

  const {
    isChoosingScope,
    editScope,
    notifyBusy,
    formActionsRef,
    handleEditClick,
    handleCancelScope,
    handleChooseScope,
    handleChangeScope,
    handleCancelEdit,
  } = useMetadataEditScopeFlow({
    resetKey: `${pageEntry?.entry.id ?? ''}:${pageNum}:${pageUserEdit?.entry.id ?? ''}`,
    hasRevisionFamily,
    arePriorEditsReady,
    onEditingChange,
  })

  const resolveMemberPriorEdit = useCallback(
    (member: PageRevisionFamilyMember) => {
      if (member.isCurrent && pageUserEdit !== undefined) {
        return pageUserEdit
      }
      return priorEditsByPageEntryId.get(member.memberEntryId)
    },
    [pageUserEdit, priorEditsByPageEntryId]
  )

  const handleFormSave = useCallback(
    async (values: DocumentPageMetadataUserEditValues) => {
      await saveMetadataEditWithScope({
        editScope,
        hasRevisionFamily,
        values,
        familyMembers: members.map((member) => ({
          bqDocumentId: member.bqDocumentId,
          pageNum: member.pageNum,
          systemPage: member.systemPage,
          priorEdit: resolveMemberPriorEdit(member),
          systemDocument:
            member.isCurrent && systemDocument !== undefined
              ? systemDocument
              : documentsByBqId.get(member.bqDocumentId),
          documentPriorEdit:
            member.isCurrent && documentUserEdit !== undefined
              ? documentUserEdit
              : priorDocumentEditsByBqDocumentId.get(member.bqDocumentId),
        })),
        onSave,
        onSaveFamily,
        mutatePriorEdits,
      })
    },
    [
      documentUserEdit,
      documentsByBqId,
      editScope,
      hasRevisionFamily,
      members,
      mutatePriorEdits,
      onSave,
      onSaveFamily,
      priorDocumentEditsByBqDocumentId,
      resolveMemberPriorEdit,
      systemDocument,
    ]
  )

  const {
    isEditing,
    editableFields,
    lockedLabels,
    showLeaveFamilyWarning,
    crossSurfaceCascadeWarning,
    beginEdit,
    cancelEdit,
    setFieldValue,
    saveEdit,
  } = usePageMetadataEditForm({
    pageEntry,
    pageNum,
    userEditValues,
    userEditEntryId: pageUserEdit?.entry.id,
    editScope,
    documentBaselineValues,
    documentTotalPages: systemDocument?.entry.data.total_pages,
    onSave: handleFormSave,
    onEditingChange: notifyBusy,
  })

  formActionsRef.current = { beginEdit, cancelEdit }

  const handleSaveClick = useCallback(() => {
    void saveEdit()
  }, [saveEdit])

  const revisionValueControl =
    showRevisionDropdown && hasRevisionFamily && onSelectVersion
      ? {
          label: PAGE_REVISION_CODE_FIELD_LABEL,
          node: (
            <PageRevisionFamilyDropdown
              members={members}
              isLoading={isFamilyLoading}
              onSelectMember={onSelectVersion}
            />
          ),
        }
      : undefined

  if (isChoosingScope) {
    return (
      <ChooseEditScopePanel
        familySize={familySize}
        currentFilename={currentFilename}
        members={members}
        familyScopeDisabled={!arePriorEditsReady}
        onCancel={handleCancelScope}
        onChooseScope={handleChooseScope}
        onSelectVersion={onSelectVersion}
        onOpenInNewTab={onOpenInNewTab}
      />
    )
  }

  return (
    <MetadataEditFormChrome
      title={t`Page Details`}
      canEdit={canEdit}
      isEditing={isEditing}
      isLoading={isLoading}
      isSaving={isSaving}
      isFamilyLoading={isFamilyLoading}
      editAriaLabel={t`Edit page metadata`}
      cancelAriaLabel={t`Cancel editing page metadata`}
      saveAriaLabel={t`Save page metadata`}
      confirmMessage={t`Saving these edits confirms that all page information is correct.`}
      saveLabel={t`Save page data`}
      familySaveLabel={t`Save all ${familySize} revisions`}
      leaveFamilyWarning={
        showLeaveFamilyWarning
          ? t`Changing Drawing # or Sheet # removes this document from the revision family.`
          : undefined
      }
      crossSurfaceCascadeWarning={crossSurfaceCascadeWarning}
      editScope={editScope}
      hasRevisionFamily={hasRevisionFamily}
      familySize={familySize}
      currentFilename={currentFilename}
      editableFields={editableFields}
      lockedLabels={lockedLabels}
      onEditClick={handleEditClick}
      onCancelEdit={handleCancelEdit}
      onSaveEdit={handleSaveClick}
      onChangeScope={handleChangeScope}
      onFieldChange={setFieldValue}>
      <PageMetadataContent
        page={pageEntry}
        pageNum={pageNum}
        isLoading={isLoading}
        userEditValues={userEditValues}
        includeInternalIds={includeInternalIds}
        valueControl={revisionValueControl}
      />
    </MetadataEditFormChrome>
  )
}
